import React from "react";
import { Chip } from "@mui/material";
import Tooltip from "../components/tooltip";

const ChipWithTooltip = [
  {
    title: ["Have a Low Word Count"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            Word count refers to the total number of words on a webpage. Search
            engines prefer pages with enough content to provide value to users.
          </em>
        </p>

        <b>Best Practice:</b>
        <p>
          <em>
            A webpage should have at least <b>300 words</b> for better indexing,
            but longer content (<b>1000+ words</b>) often ranks better.
          </em>
        </p>
      </>
    ),
  },
  {
    title: [
      "Duplicate Meta Description",
      "With Description too short",
      "With Description too long",
      "Without Meta Description",
    ],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            A meta description is a short summary of a webpage’s content
            displayed in search engine results.
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>
              Keep it between <b>150-160</b> characters.
            </em>
          </li>
          <li>
            <em>Include primary keywords naturally.</em>
          </li>
          <li>
            <em>Make it engaging to encourage clicks.</em>
          </li>
        </ul>
        <br></br>
        <b>Example:</b>
        <p>
          <code>
            &lt;meta name="description" content="Learn SEO best practices,
            including keyword optimization, meta descriptions, and backlinks to
            improve your website ranking."&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: [
      "Duplicate Title tags",
      "With no Title Tags",
      "With Title tag that is too long",
      "With Title tag that is too short",
    ],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            A title tag is the clickable headline displayed on search engine
            results pages (SERPs).
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>
              Keep it under <b>60</b> characters.
            </em>
          </li>
          <li>
            <em>Include primary keywords near the beginning.</em>
          </li>
        </ul>

        <br></br>
        <b>Example:</b>
        <p>
          <code>
            &lt;title&gt;SEO Best Practices for Higher Google
            Rankings&lt;/title&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: ["Without H1 Heading", "Multiple H1 tag"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            The H1 tag is the main heading of a webpage, telling search engines
            what the page is about.
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>
              Use only <b>one H1</b> per page.
            </em>
          </li>
          <li>
            <em>Include main keywords.</em>
          </li>
        </ul>

        <br></br>
        <b>Example:</b>
        <p>
          <code>&lt;h1&gt;Best SEO Practices for 2024&lt;/h1&gt;</code>
        </p>
      </>
    ),
  },
  {
    title: ["With Broken Links"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            A broken link is a hyperlink that no longer works, leading to 404
            errors.
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>
              Regularly check and fix broken links using tools like Google
              Search Console.
            </em>
          </li>
        </ul>

        <br></br>
        <b>Example:</b>
        <p>
          ❌ <b>Broken Link:</b>
          <br></br>
          <code>
            &lt;a href="https://oldsite.com/page"&gt;Click Here&lt;/a&gt;
            &lt;!-- (Page does not exist) --&gt;
          </code>
        </p>

        <p>
          ✅ <b>Fixed Link:</b>
          <br></br>
          <code>
            &lt;a href="https://newsite.com/page"&gt;Click Here&lt;/a&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: ["Duplicate H1 and Title Tag"],
  },
  {
    title: ["No canonical tag"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            A canonical tag prevents duplicate content issues by specifying the
            preferred URL for a page.
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>Use it if the same content exists on multiple URLs.</em>
          </li>
        </ul>

        <br></br>
        <b>Example:</b>
        <p>
          <code>
            &lt;link rel="canonical" href="https://example.com/seo-guide"&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: ["No favicon"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            A favicon is the small icon displayed in the browser tab next to the
            website title.
          </em>
        </p>

        <b>Example:</b>
        <p>
          <code>
            &lt;link rel="icon" href="/favicon.ico" type="image/x-icon"&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: ["No image alt"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            Alt text (alternative text) is used to describe images, helping
            search engines and visually impaired users understand the content.
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>Use descriptive and keyword-rich alt text.</em>
          </li>
          <li>
            <em>Avoid keyword stuffing.</em>
          </li>
        </ul>

        <br></br>
        <b>Example:</b>
        <p>
          <code>
            &lt;img src="seo-guide.jpg" alt="SEO guide for beginners covering
            meta tags and keywords"&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: ["No image title"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            The image title is a tooltip shown when users hover over an image.
          </em>
        </p>

        <b>Example:</b>
        <p>
          <code>
            &lt;img src="seo-guide.jpg" alt="SEO Guide" title="SEO Best
            Practices Guide"&gt;
          </code>
        </p>
      </>
    ),
  },
  {
    title: ["Poorly formatted URL for SEO", "URLs are too long"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>A well-structured URL improves SEO and readability.</em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>Keep URLs short and descriptive.</em>
          </li>
          <li>
            <em>Use hyphens (-) instead of underscores (_).</em>
          </li>
        </ul>

        <br></br>
        <b>Example:</b>
        <p>
          ✅ <b>Good:</b>
          <br></br>
          <code>https://example.com/seo-tips</code>
        </p>

        <p>
          ❌ <b>Bad:</b>
          <br></br>
          <code>https://example.com/seo_tips_2024.html?id=1234</code>
        </p>
      </>
    ),
  },
  {
    title: ["Doctype not Declared"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            The Doctype declaration defines the document type and version of
            HTML being used.
          </em>
        </p>

        <b>Example:</b>
        <p>
          <code>&lt;!DOCTYPE html&gt;</code>
        </p>
      </>
    ),
  },
  {
    title: ["Long Loading Time"],
    tooltip: (
      <>
        <b>What is it?</b>
        <p>
          <em>
            Loading time refers to how quickly a webpage loads. Faster pages
            rank better and provide a better user experience.
          </em>
        </p>

        <b>Best Practice:</b>
        <ul>
          <li>
            <em>Compress images.</em>
          </li>
          <li>
            <em>Minify CSS & JavaScript.</em>
          </li>
          <li>
            <em>Use a CDN (Content Delivery Network).</em>
          </li>
        </ul>
      </>
    ),
  },
];

const GetSeverity = ({ issuesType }) => {
  const isTooltip = ChipWithTooltip?.find((item) =>
    item.title?.includes(issuesType.td1)
  );
  return (
    <>
      {isTooltip?.tooltip && <Tooltip title={isTooltip.tooltip} />}
      {issuesType.severity && (
        <Chip
          label={issuesType.severity}
          size="small"
          className="heading-xs ms-2"
          sx={{
            height: "18px",
            backgroundColor: issuesType.bgColor,
            "& .MuiChip-label": {
              color: issuesType.color,
              fontWeight: "500",
              fontFamily: "Work Sans",
            },
          }}
        />
      )}
    </>
  );
};

export default GetSeverity;
